import { useContext, useMemo, useState } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import RenameModal from '@atom/components/common/RenameModal';
import addItemIcon from '@atom/components/common/svgIcons/add_subItem.svg';
import TextOverflowTooltip from '@atom/components/common/tooltip/TextOverflowTooltip';
import WorkOrderAssetViewContext from '@atom/components/common/workOrderDetail/workOrderAssetView/WorkOrderAssetViewContext';
import { useSchemas } from '@atom/hooks/useSchemas';
import { Icon, IconButton, Menu } from '@atom/mui';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import {
  AttributeGroupsItem,
  ChangeType,
  ElementGroupsTreeItem,
} from '@atom/types/inventory';
import { WorkOrderAssetTreeElement } from '@atom/types/work';
import api from '@atom/utilities/api';
import { assetTreeHasDownstreamChanges } from '@atom/utilities/assetUtilities';
import attributeDisplayUtility from '@atom/utilities/attributeDisplayUtility';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { WORK_ORDERS_ENDPOINT } from '@atom/utilities/endpoints';
import iconUtilities from '@atom/utilities/iconUtilities';
import schemaUtilities from '@atom/utilities/schemaUtilities';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import AddElementModal from './addElementModal/AddElementModal';
import ElementControls from './ElementControls';
import ElementGroupRow from './ElementGroupRow';
import ElementRow from './ElementRow';

import classes from './workOrderAssetTree.module.css';

const { MenuItem } = Menu;

const styles = {
  elementIconStyle: {
    height: '30px',
    width: '30px',
  },
};

enum ModalType {
  ADD_ELEMENT = 'ADD_ELEMENT',
  EDIT = 'EDIT',
  NONE = 'NONE',
}
type OpenModal = { type: ModalType; id: string | null };

const WorkOrderAssetTree = () => {
  const { schemas = [] } = useSchemas();

  const {
    workOrderAsset,
    workOrderDetail,
    refetchWorkOrderAssetTree,
    setActiveElementId,
    activeElementId,
    previewView,
    canAddAsset,
  } = useContext(WorkOrderAssetViewContext);

  const rootSchema = useMemo(() => {
    return schemas.find(
      ({ id }) => id === workOrderDetail.inventoryAssetSchemaId,
    );
  }, [schemas, workOrderDetail.inventoryAssetSchemaId]);

  const [expanded, setExpanded] = useState<boolean>(true);
  const [openModal, setOpenModal] = useState<OpenModal>({
    type: ModalType.NONE,
    id: null,
  });

  const hasPendingChanges = useMemo(() => {
    return (
      !!workOrderAsset.changeType &&
      workOrderAsset.changeType !== ChangeType.ELEMENTS_CHANGED &&
      workOrderAsset.changeType !== ChangeType.PARENT_ADDED
    );
  }, [workOrderAsset]);

  const hasDownstreamChanges = useMemo(() => {
    return assetTreeHasDownstreamChanges(workOrderAsset);
  }, [workOrderAsset]);

  const arrowDirection = expanded ? 'arrow_drop_down' : 'arrow_right';

  const renameElement = async (id: string, name: string) => {
    const endpoint = `${WORK_ORDERS_ENDPOINT}/${workOrderDetail.id}/assets/${id}`;
    await api.patch(endpoint, { name });

    refetchWorkOrderAssetTree();
  };

  const renderExpandedArrow =
    !R.isNil(workOrderAsset.elements) && !R.isEmpty(workOrderAsset.elements);

  const showControls =
    hasRolePermissions(ROLE_SETS.MANAGER) && hasPendingChanges && !previewView;

  const showAdditionalOptions =
    hasRolePermissions(ROLE_SETS.INSPECTOR) && !workOrderDetail.isClosed;

  const isRootAsset = workOrderDetail.inventoryAssetId === workOrderAsset.id;

  const isModalOpen = (modalType: ModalType, id: string) => {
    return openModal.type === modalType && openModal.id === id;
  };

  const getAttributeSubText = (): string => {
    if (
      R.isEmpty(workOrderAsset.attributeGroups) ||
      R.isNil(workOrderAsset.attributeGroups)
    ) {
      return '';
    }

    const attributes = workOrderAsset.attributeGroups.reduce(
      (acc: string[], group: AttributeGroupsItem): string[] => {
        return [...acc, ...group.attributes];
      },
      [],
    );

    return attributes.reduce((acc: string, attributeId: string): string => {
      const attribute = workOrderAsset.attributes[attributeId];
      if (!attribute.isVisibleAsSubtext) {
        return acc;
      }

      const label = attribute.name;
      const value = attributeDisplayUtility.display(
        attribute.value,
        attribute.dataType,
        attribute.unit,
      );

      const text = `${label}: ${value}`;
      return acc ? `${acc}, ${text}` : text;
    }, '');
  };

  const attributeSubtext = getAttributeSubText();
  const subtext = isNilOrEmpty(attributeSubtext)
    ? workOrderAsset.assetType
    : `${workOrderAsset.assetType} \u00B7 ${attributeSubtext}`;

  return (
    <>
      <div
        className={clsx(
          classes.assetRow,
          workOrderAsset.id === activeElementId && classes.selected,
        )}
        onClick={() => setActiveElementId(workOrderAsset.id)}
      >
        <div className={classes.assetNameContainer}>
          {renderExpandedArrow && (
            <IconButton onClick={() => setExpanded(!expanded)}>
              <Icon>{arrowDirection}</Icon>
            </IconButton>
          )}
          <div className={classes.iconContainer}>
            {isRootAsset ? (
              <div style={styles.elementIconStyle}>
                {schemaUtilities.getSchemaIconFromSchemaOrAsset(rootSchema, {
                  imgOffset: '0',
                  iconSize: '34px',
                  marginTop: '0',
                })}
              </div>
            ) : (
              iconUtilities.getDataManagementElementIcon(
                null,
                hasPendingChanges,
              )
            )}
            {hasDownstreamChanges && (
              <div className={clsx(classes.changeIndicator, classes.icon)} />
            )}
          </div>
          <div className={classes.rowNameContainer}>
            <div
              className={clsx(
                classes.rowName,
                hasPendingChanges && classes.blue,
              )}
            >
              <span className={classes.rowNameText}>{workOrderAsset.name}</span>
            </div>
            <div className={clsx(classes.rowName, classes.subtext)}>
              <TextOverflowTooltip
                text={subtext}
                width="100%"
                bold={false}
                fontSize={fonts.sm}
                color={colors.neutral.gray}
                lightTooltip
              />
            </div>
          </div>
        </div>
        <div className={classes.controlsContainer}>
          {showControls && (
            <ElementControls
              // @ts-ignore
              element={workOrderAsset}
            />
          )}
          {showAdditionalOptions && (
            <>
              <Menu>
                {canAddAsset && (
                  <MenuItem
                    startAdornment={
                      <Icon>
                        <img src={addItemIcon} />
                      </Icon>
                    }
                    onClick={() =>
                      setOpenModal({
                        type: ModalType.ADD_ELEMENT,
                        id: workOrderAsset.id,
                      })
                    }
                  >
                    Add Sub Items
                  </MenuItem>
                )}
                <MenuItem
                  startAdornment={<Icon>edit</Icon>}
                  onClick={() =>
                    setOpenModal({
                      type: ModalType.EDIT,
                      id: workOrderAsset.id,
                    })
                  }
                >
                  Edit
                </MenuItem>
              </Menu>
              <AddElementModal
                open={isModalOpen(ModalType.ADD_ELEMENT, workOrderAsset.id)}
                closeModal={() =>
                  setOpenModal({ type: ModalType.NONE, id: null })
                }
                // @ts-ignore
                element={workOrderAsset}
              />
              <RenameModal
                open={isModalOpen(ModalType.EDIT, workOrderAsset.id)}
                closeModal={() =>
                  setOpenModal({ type: ModalType.NONE, id: null })
                }
                id={workOrderAsset.id}
                name={workOrderAsset.name}
                type="element"
                renameAction={renameElement}
              />
            </>
          )}
        </div>
      </div>
      {expanded ? (
        <>
          {workOrderAsset.elementGroups.map(
            (elementGroup: ElementGroupsTreeItem, index: number) => (
              <ElementGroupRow
                key={`${elementGroup.name}-${index}`}
                elementGroup={elementGroup}
                indentation={1}
              />
            ),
          )}
          {workOrderAsset.elements.map(
            (element: WorkOrderAssetTreeElement, index: number) => (
              <ElementRow
                key={`${element.name}-${index}`}
                element={element}
                indentation={1}
              />
            ),
          )}
        </>
      ) : (
        <div />
      )}
    </>
  );
};

export default WorkOrderAssetTree;
